/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
// We are using an anchor tag for a link/button

import React, { DOMAttributes, memo } from 'react'
import classNames from 'classnames'
import LockedIcon from 'transfix-icons/build/LockedIcon'
import Tooltip from 'transfix-ui/components/Tooltip'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { INavRoute } from 'types/routes'

interface ISubRouteProps {
  route: INavRoute
  onClose: () => void
}

const ICON_WIDTH_PX = 32

const useSubRouteStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flex: '0 0 100%',
    width: '100%',
    flexDirection: 'row',
    position: 'relative',
    flexWrap: 'wrap',
    backgroundColor: brandColors.white,
    color: brandColors.black,
    alignItems: 'center',
    padding: 0,
  },
  link: {
    flex: '0 0 100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    color: brandColors.coolGray8,

    '&:hover, &:active, &:focus': {
      textDecoration: 'none',
      color: brandColors.coolGray8,
      backgroundColor: brandColors.coolGray1,
    },
  },
  linkLocked: {
    '&:hover, &:active, &:focus': {
      cursor: 'default',
      textDecoration: 'none',
      color: brandColors.coolGray8,
      backgroundColor: brandColors.white,
    },
  },
  linkActive: {
    color: brandColors.skyBlue6,
    backgroundColor: brandColors.skyBlue1,

    '&:hover, &:active': {
      textDecoration: 'none',
      color: brandColors.skyBlue7,
      backgroundColor: brandColors.skyBlue2,
    },

    '&:focus': {
      textDecoration: 'none',
      backgroundColor: brandColors.skyBlue2,
      color: brandColors.skyBlue7,
    },
  },
  text: {
    fontWeight: 600,
  },
  textLocked: {
    color: brandColors.coolGray5,
  },
  iconWrapper: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    /**
     * @note Imitates the width of icons in other elements
     */
    width: ICON_WIDTH_PX,
  },
  textWrapper: {
    flex: '1 1 auto',
    display: 'flex',
    alignItems: 'center',
  },
  lockedIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const SubRoute = memo<ISubRouteProps>(({ onClose, route }) => {
  const { isActive, isLocked = false, location, name } = route || {}
  const classes = useSubRouteStyles()

  const clickHandler: DOMAttributes<HTMLAnchorElement>['onClick'] = () => {
    if (isLocked) {
      return
    }

    if (location) {
      window.location.href = location
      onClose()
    }
  }

  const textClassName = classNames(classes.text, { [classes.textLocked]: isLocked })
  const linkClassName = classNames(classes.link, {
    [classes.linkLocked]: isLocked,
    [classes.linkActive]: isActive,
  })

  return (
    <li data-test='SubRoute' className={classes.root}>
      <Tooltip
        effect='solid'
        id={location}
        position='left'
        disable={!isLocked}
        offset={{
          right: 32,
        }}
        trigger={
          <a onClick={clickHandler} className={linkClassName} role='link' tabIndex={0}>
            <div className={classes.iconWrapper} />
            <div className={classes.textWrapper}>
              <Typography variant='body2' className={textClassName} component='div'>
                {name}
              </Typography>
              {isLocked && (
                <div className={classes.lockedIconWrapper}>
                  <LockedIcon color='coolGray5' size='medium' />
                </div>
              )}
            </div>
          </a>
        }
        type='dark'>
        <div style={{ whiteSpace: 'nowrap' }}>Admin permission required</div>
      </Tooltip>
    </li>
  )
})

export default SubRoute
